import type { TaskTypeName } from 'src/gql/graphql';

import type { TaskTypeInfo } from '../components/advisor-dashboard/account-overview/create-task-dialog-types';

export enum XLR8CS__Category__c {
  ClientOperations = 'Client Operations',
  ClientServices = 'Client Services',
  SelfReminder = 'Self Reminder',
  InternalOperations = 'Internal Operations',
  Personal = 'Personal',
  OpsTask = 'Ops Task',
  ClientTask = 'Client Task',
}

export enum XLR8CS__Sub_Category_Task__c {
  ClientIntake = 'Client: Intake',
  ClientOnboarding = 'Client: Onboarding',
  ClientPreReviewCheckIn = 'Client: Pre-Review Check In',
  ClientFileUpload = 'Client: File Upload',
  ClientOther = 'Client: Other',
  ClientAccountConfirmation = 'Client: Account Confirmation',
  ClientRiskQuestionnaire = 'Client: Risk Questionnaire',
  SelfReminder = 'Self Reminder',
  Other = 'Other',
  OtherInvestmentTask = 'Other Investment Task',
  //TODO: [Ted] deprecate below after new MM is set up, also need to deprecate "Money movement"
  MoneyMovement = 'Ops: Money movement',
  NewMoneyMovement = 'Ops: Money Movement Request',
  DEPRECEATED_MoneyMovement = 'Money movement',
  Trading = 'Ops: Trading Request',
  AccountMaintenance = 'Account maintenance',
  ClientInfoChange = 'Client info change',
  FinnancialPlanning = 'Financial planning',
  InvestmentManagementDirectIndexing = 'Investment management: Direct indexing',
  InvestmentManagementSWIM = 'Ops: Swim Request',
  ClientReviewPresentation = 'Client review presentation',
  InvestmentProposalPresentation = 'Investment proposal presentation',
  TransitionAnalysis = 'Ops: Transition Analysis',
}

export enum SalesforceTaskStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  WaitingOnSomeoneElse = 'Waiting on someone else',
  Deferred = 'Deferred',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

export const CLIENT_INTAKE_TASK: TaskTypeInfo = {
  name: 'Intake',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientIntake,
  description: 'Send prospects a questionnaire to collect statements and learn more about them',
};

export const CLIENT_ACCOUNT_CONFIRMATION_TASK: TaskTypeInfo = {
  name: 'Account confirmation',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientAccountConfirmation,
  description: 'Obtain necessary information from client before onboarding',
};

export const CLIENT_ONBOARDING_TASK: TaskTypeInfo = {
  name: 'Onboarding',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientOnboarding,
  description: 'Send clients the IAA and account opening documents to be signed',
};

export const CLIENT_PRE_REVIEW_CHECK_IN_TASK: TaskTypeInfo = {
  name: 'Pre-review check in',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientPreReviewCheckIn,
  description: 'Collect up-to-date information in prep for a financial review',
};

export const CLIENT_FILE_UPLOAD_TASK: TaskTypeInfo = {
  name: 'File upload',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientFileUpload,
  description: 'Ask a client to upload files to their Box account',
};

export const MONEY_MOVEMENT_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.MoneyMovement,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.MoneyMovement,
  customName: 'Money movement',
  description: 'Make inbound or outbound transactions on a client account',
  flowDescription: 'Make inbound or outbound transactions on a client account',
};

export const TRADING_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.Trading,
  customName: 'Trading',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.Trading,
  description: 'Buy and sell multiple holdings for a client portfolio',
};

export const ACCOUNT_MAINTENANCE_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.AccountMaintenance,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.AccountMaintenance,
  description: 'Open, close, move or consolidate an account',
  flowDescription: 'Open a new account or close, move or consolidate an existing account for your client',
};

export const INVESTMENT_MANAGEMENT_DIRECT_INDEXING_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing,
  customName: 'Direct Indexing',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing,
  description: 'Set up or change a direct index account',
};

export const INVESTMENT_MANAGEMENT_SWIM_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.InvestmentManagementSWIM,
  customName: 'Savvy Wealth Investment Management (SWIM)',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.InvestmentManagementSWIM,
  description: 'Enroll a client into the Savvy Wealth Investment Management Program',
};

export const INTERNAL_CLIENT_REVIEW_PRESENTATION_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.ClientReviewPresentation,
  customName: 'Client review presentation',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientReviewPresentation,
  description: 'Request a Savvy branded deck for client reviews',
};

export const TRANSITION_ANALYSIS_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.TransitionAnalysis,
  customName: 'Portfolio Review',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.TransitionAnalysis,
  description:
    'Request an investment proposal, a transition analysis, or both — comparing current holdings to a recommended model and projecting trades and capital gains',
};

export const INVESTMENT_PROPOSAL_PRESENTATION_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.InvestmentProposalPresentation,
  customName: 'Investment proposal presentation',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.InvestmentProposalPresentation,
  description: 'Generate a Savvy branded deck for a client or prospect using our investment proposal tool',
};

export const INTERNAL_CLIENT_INFO_CHANGE_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.ClientInfoChange,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientInfoChange,
  description: 'Update information across platforms, e.g. new address or last name',
  flowDescription:
    'When client information changes, for example a new address or last name, the Savvy Client Services team will ensure that client information is updated across all platforms',
};

export const FINNANCIAL_PLANNING_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.FinnancialPlanning,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.FinnancialPlanning,
  description: 'Request financial planning tool-related support',
};

export const INTERNAL_OTHER_INVESTMENT_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.OtherInvestmentTask,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.OtherInvestmentTask,
  description: 'Anything else for the Investment team',
  flowDescription:
    'Any trading or investment related task not covered elsewhere. These tasks are directed straight to the investment ops team.',
};

export const INTERNAL_OTHER_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.Other,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.Other,
  description: 'Anything else for the Client Services team',
  flowDescription:
    'For example, ask for a report to be generated, contact a client about a topic, resolve an issue with Schwab, organize some files for a client, or something else.',
};

export const ADVISOR_SELF_REMINDER_TASK: TaskTypeInfo = {
  name: XLR8CS__Sub_Category_Task__c.SelfReminder,
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.SelfReminder,
  description: 'Self Reminder',
};

export const CLIENT_RISK_QUESTIONNAIRE_TASK: TaskTypeInfo = {
  name: 'Risk Questionnaire',
  salesforceSubCategory: XLR8CS__Sub_Category_Task__c.ClientRiskQuestionnaire,
  description: 'Send clients a risk assessment questionnaire to determine their investment risk tolerance',
};

export const CLIENT_TASKS: TaskTypeInfo[] = [
  CLIENT_INTAKE_TASK,
  CLIENT_ACCOUNT_CONFIRMATION_TASK,
  CLIENT_ONBOARDING_TASK,
  CLIENT_FILE_UPLOAD_TASK,
  CLIENT_PRE_REVIEW_CHECK_IN_TASK,
  CLIENT_RISK_QUESTIONNAIRE_TASK,
];

export const INTERNAL_TASKS: TaskTypeInfo[] = [
  MONEY_MOVEMENT_TASK,
  ACCOUNT_MAINTENANCE_TASK,
  INTERNAL_CLIENT_INFO_CHANGE_TASK,
  FINNANCIAL_PLANNING_TASK,
  INVESTMENT_MANAGEMENT_DIRECT_INDEXING_TASK,
  INVESTMENT_MANAGEMENT_SWIM_TASK,
  INTERNAL_CLIENT_REVIEW_PRESENTATION_TASK,
  INVESTMENT_PROPOSAL_PRESENTATION_TASK,
  CLIENT_RISK_QUESTIONNAIRE_TASK,
  INTERNAL_OTHER_TASK,
  INTERNAL_OTHER_INVESTMENT_TASK,
];

const SAVVY_TASK_TYPE_NAME_TO_SALESFORCE_TASK_BY_TYPE = {
  initial_intake_form: CLIENT_INTAKE_TASK,
  account_setup: CLIENT_ACCOUNT_CONFIRMATION_TASK,
  onboarding_flow: CLIENT_ONBOARDING_TASK,
  account_onboarding: CLIENT_ONBOARDING_TASK,
  pre_review_check_in: CLIENT_PRE_REVIEW_CHECK_IN_TASK,
  file_upload: CLIENT_FILE_UPLOAD_TASK,
  money_movement_request: MONEY_MOVEMENT_TASK,
  trading_request: TRADING_TASK,
  account_maintenance: ACCOUNT_MAINTENANCE_TASK,
  swim_request: INVESTMENT_MANAGEMENT_SWIM_TASK,
  client_review_presentation: INTERNAL_CLIENT_REVIEW_PRESENTATION_TASK,
  investment_proposal_presentation: INVESTMENT_PROPOSAL_PRESENTATION_TASK,
  client_info_change: INTERNAL_CLIENT_INFO_CHANGE_TASK,
  financial_planning: FINNANCIAL_PLANNING_TASK,
  other: INTERNAL_OTHER_TASK,
  other_investment_task: INTERNAL_OTHER_INVESTMENT_TASK,
  self_reminder: ADVISOR_SELF_REMINDER_TASK,
  // needed to satisfy typescript type checker
  client_review: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  client_review_meeting: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  client_review_meeting_material_request: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  investment_proposal_generation: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  investment_proposal_request: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  client_other: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  client_transition_flow: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  contract: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  contract_change: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  data_info_maintenance: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  emoney: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  investment_management: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  investment_management_direct_indexing: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  investment_management_wealthcare: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  marketing: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  reporting: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  trades_cash_management: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  transfer: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  risk_questionnaire: CLIENT_RISK_QUESTIONNAIRE_TASK,
  birthday_reminder: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  transition_analysis: {
    name: 'Transition Analysis',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  open_new_account: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  transfer_of_assets: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  alternative_investments_purchase: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  eci_household_onboarding: {
    name: '',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  workflow: {
    name: 'Workflow',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  general: {
    name: 'General',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  workflow_template: {
    name: 'Workflow Template',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  rejected_fee: {
    name: 'Rejected Fee',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  client_billing: {
    name: 'Client Billing',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
  corporate_retirement_plan: {
    name: 'Corporate Retirement Plans',
    customName: undefined,
    salesforceSubCategory: undefined,
    description: '',
    flowDescription: undefined,
  },
};

export const SAVVY_TASK_TYPE_NAME_TO_SALESFORCE_TASK: Record<TaskTypeName, TaskTypeInfo> =
  SAVVY_TASK_TYPE_NAME_TO_SALESFORCE_TASK_BY_TYPE;
